/**
 * Step 5 — Time slot picker (+ contact details for anonymous users).
 *
 * The backend returns a flat array of "YYYY-MM-DD HH:mm:ss" strings. We
 * group them by date so the user sees a clear "Today / Tomorrow / Mon Jun 13"
 * style layout with clickable time chips per day.
 *
 * If the user is logged in we already have their email; for anonymous users
 * we collect email + optional name + phone on this step. The contact fields
 * are forwarded to BookingPage but currently dropped before the create-ticket
 * request — the backend doesn't accept `client_name` / `phone` / `email`
 * yet, so we hold onto them locally until that lands.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchTimeSuggestions,
  getServiceCategoryId,
} from "../../helper/bookingApi";
import {
  EmptyState,
  RetryButton,
  StepCard,
  StepFooter,
  StepHeader,
  STEP_PAD,
} from "./BookingPrimitives";
import {
  dayKey,
  formatClockTime,
  formatDayLabel,
  formatMonth,
  formatWeekday,
  parseSlotDate,
} from "./bookingFormat";

function emailIsValid(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function phoneIsValid(value) {
  // Accept anything that contains 6+ digits (allowing +, spaces, parens,
  // dashes). The backend will normalize, so we keep the UI permissive.
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 6;
}

export function TimeSlotStep({
  palette,
  lang,
  branch,
  service,
  user,
  isAuthenticated,
  userLoading = false,
  accountContact = {},
  selectedSlot,
  onSelectSlot,
  email,
  onEmailChange,
  clientName,
  onClientNameChange,
  phone,
  onPhoneChange,
  clientComment,
  onClientCommentChange,
  onBack,
  onNext,
}) {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState("");
  const [requestedDayKey, setRequestedDayKey] = useState("");
  const dayScrollerRef = useRef(null);
  const dayScrollTimerRef = useRef(null);
  const dayScrollPointerRef = useRef(false);

  const branchId = branch?.id;
  // The selected service may originate from either a top-level Category
  // record (where `id` IS the category id) or a drill-down BranchCategory
  // record (where `id` is the branch_category row id and the real
  // category id is on `category_id`). Use the helper so we always send
  // the id the time-suggestions API expects.
  const categoryId =
    service?.categoryId ?? getServiceCategoryId(service);

  const load = useCallback(async () => {
    if (!branchId || !categoryId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTimeSuggestions(lang, {
        branchId,
        categoryId,
        date: requestedDayKey || undefined,
      });
      const parsed = (Array.isArray(data?.data) ? data.data : [])
        .map((s) => ({ raw: s, date: parseSlotDate(s) }))
        .filter((s) => s.date != null);
      setSlots(parsed);
    } catch (err) {
      console.error("[booking] slots load failed", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [branchId, categoryId, lang, requestedDayKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedDayKey("");
    setRequestedDayKey("");
  }, [branchId, categoryId]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = dayKey(s.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    // Sort each day's slots, then return in date order
    return Array.from(map.entries())
      .map(([key, list]) => ({
        key,
        date: list[0].date,
        slots: [...list].sort((a, b) => a.date - b.date),
      }))
      .sort((a, b) => a.date - b.date);
  }, [slots]);

  // const dayOptions = useMemo(() => {
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);
  //   const slotMap = new Map(grouped.map((group) => [group.key, group]));
  //   const options = [];

  //   for (let i = 0; i < 30; i += 1) {
  //     const date = new Date(today);
  //     date.setDate(today.getDate() + i);
  //     const key = dayKey(date);
  //     const existing = slotMap.get(key);
  //     options.push(existing || { key, date, slots: [] });
  //   }

  //   for (const group of grouped) {
  //     if (!options.some((option) => option.key === group.key)) {
  //       options.push(group);
  //     }
  //   }

  //   return options;
  // }, [grouped]);


  const dayOptions = useMemo(() => {
  if (!grouped.length) return [];

  const firstAvailableDate = new Date(grouped[0].date);
  firstAvailableDate.setHours(0, 0, 0, 0);

  const slotMap = new Map(grouped.map((group) => [group.key, group]));
  const options = [];

  for (let i = 0; i < 30; i += 1) {
    const date = new Date(firstAvailableDate);
    date.setDate(firstAvailableDate.getDate() + i);

    const key = dayKey(date);
    const existing = slotMap.get(key);

    options.push(
      existing || {
        key,
        date,
        slots: [],
      },
    );
  }

  return options;
}, [grouped]);

  useEffect(() => {
    if (requestedDayKey) {
      if (selectedDayKey !== requestedDayKey) setSelectedDayKey(requestedDayKey);
      return;
    }
    if (selectedSlot?.date) {
      setSelectedDayKey(dayKey(selectedSlot.date));
      return;
    }
    if (!selectedDayKey && (grouped[0]?.key || dayOptions[0]?.key)) {
      setSelectedDayKey(grouped[0]?.key || dayOptions[0].key);
    }
  }, [dayOptions, grouped, loading, requestedDayKey, selectedDayKey, selectedSlot]);

  const selectedGroup = useMemo(() => {
    return dayOptions.find((group) => group.key === selectedDayKey) || dayOptions[0];
  }, [dayOptions, selectedDayKey]);

  const accountEmail = accountContact.email || user?.email || "";
  const accountPhone = accountContact.phone || user?.phone || user?.phone_number || "";
  const accountName = accountContact.name || user?.name || "";
  const effectiveName = clientName || accountName || "";
  const effectiveEmail = email || accountEmail || "";
  const effectivePhone = phone || accountPhone || "";
  const hasCompleteAccountContact =
    Boolean(isAuthenticated && accountName && accountEmail && accountPhone);
  const canAskForMissingContact = !isAuthenticated || !userLoading;
  const shouldAskName = canAskForMissingContact && (!isAuthenticated || !accountName);
  const shouldAskEmail = canAskForMissingContact && (!isAuthenticated || !accountEmail);
  const shouldAskPhone = canAskForMissingContact && (!isAuthenticated || !accountPhone);
  const phoneFilled = String(effectivePhone).trim().length > 0;
  const nameValid =
    !shouldAskName || String(effectiveName).trim().length > 0;
  const phoneValid = phoneFilled && phoneIsValid(effectivePhone);
  const showContactInputs =
    !hasCompleteAccountContact &&
    (shouldAskName || shouldAskEmail || shouldAskPhone);
  const emailValid =
    !shouldAskEmail || emailIsValid(effectiveEmail);
  const contactReady =
    hasCompleteAccountContact ||
    (canAskForMissingContact && nameValid && emailValid && phoneValid);
  const canContinue =
    Boolean(selectedSlot) && contactReady && !loading && !error;

  const handleConfirm = useCallback(() => {
    if (shouldAskName) setNameTouched(true);
    if (shouldAskEmail) setEmailTouched(true);
    if (shouldAskPhone) setPhoneTouched(true);
    if (!canContinue) return;
    onNext();
  }, [canContinue, onNext, shouldAskEmail, shouldAskName, shouldAskPhone]);

  const scrollDays = useCallback((direction) => {
    dayScrollerRef.current?.scrollBy({
      left: direction * 420,
      behavior: "smooth",
    });
  }, []);

  const stopDayAutoScroll = useCallback(() => {
    if (!dayScrollTimerRef.current) return;
    window.clearInterval(dayScrollTimerRef.current);
    dayScrollTimerRef.current = null;
  }, []);

  const startDayAutoScroll = useCallback((direction) => {
    stopDayAutoScroll();
    dayScrollTimerRef.current = window.setInterval(() => {
      dayScrollerRef.current?.scrollBy({ left: direction * 16, behavior: "auto" });
    }, 20);
  }, [stopDayAutoScroll]);

  const handleDayScrollClick = useCallback((direction) => {
    stopDayAutoScroll();
    scrollDays(direction);
  }, [scrollDays, stopDayAutoScroll]);

  const handleDayScrollPointer = useCallback((direction) => {
    dayScrollPointerRef.current = true;
    handleDayScrollClick(direction);
  }, [handleDayScrollClick]);

  const handleDayScrollKeyboardClick = useCallback((direction) => {
    if (dayScrollPointerRef.current) {
      dayScrollPointerRef.current = false;
      return;
    }
    handleDayScrollClick(direction);
  }, [handleDayScrollClick]);

  useEffect(() => stopDayAutoScroll, [stopDayAutoScroll]);

  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.datetime.title")}
        subtitle={t("booking.datetime.subtitle")}
      />

      <div style={{ padding: `8px ${STEP_PAD.x} 4px ${STEP_PAD.x}` }}>
        {loading && slots.length === 0 && !requestedDayKey ? (
          <div className="d-grid gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div
                  style={{
                    width: 120,
                    height: 14,
                    borderRadius: 6,
                    background: palette.surfaceAlt,
                    border: palette.border,
                    marginBottom: 10,
                  }}
                />
                <div className="d-flex flex-wrap gap-2">
                  {[0, 1, 2, 3, 4].map((j) => (
                    <div
                      key={j}
                      style={{
                        width: "clamp(72px, 18vw, 88px)",
                        height: 38,
                        borderRadius: 999,
                        background: palette.surfaceAlt,
                        border: palette.border,
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadSlots")}
            action={<RetryButton palette={palette} onClick={load} />}
          />
        ) : grouped.length === 0 && !requestedDayKey ? (
          <EmptyState
            palette={palette}
            icon="las la-clock"
            title={t("booking.datetime.noSlots")}
          />
        ) : (
          <div className="d-grid gap-3">
            <div
              style={{
                position: "relative",
                overflow: "hidden",
                minHeight: 112,
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 64,
                  zIndex: 1,
                  pointerEvents: "none",
                  background: `linear-gradient(90deg, ${palette.surface} 0%, rgba(0,0,0,0) 100%)`,
                }}
              />
              <button
                type="button"
                onPointerDown={() => handleDayScrollPointer(-1)}
                onClick={() => handleDayScrollKeyboardClick(-1)}
                onMouseEnter={() => startDayAutoScroll(-1)}
                onMouseLeave={stopDayAutoScroll}
                onFocus={() => startDayAutoScroll(-1)}
                onBlur={stopDayAutoScroll}
                aria-label="Scroll days left"
                style={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  width: 38,
                  height: 38,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  border: palette.border,
                  background: palette.surface,
                  color: palette.accent,
                  boxShadow: "0 12px 26px rgba(0,0,0,0.18)",
                  cursor: "pointer",
                }}
              >
                <i className="las la-angle-left" aria-hidden="true" />
              </button>
              <div
                ref={dayScrollerRef}
                className="booking-day-strip"
                style={{
                  display: "flex",
                  gap: 10,
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollBehavior: "smooth",
                  padding: "2px 58px 10px",
                  minHeight: 110,
                  alignItems: "stretch",
                }}
              >
                {dayOptions.map((group) => {
                  const isSelected = group.key === selectedGroup?.key;
                  const hasSlots = group.slots.length > 0;
                  return (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => {
                        setSelectedDayKey(group.key);
                        setRequestedDayKey(group.key);
                        if (selectedSlot) onSelectSlot(null);
                      }}
                      className="fw-700"
                      style={{
                        minWidth: 86,
                        height: 100,
                        boxSizing: "border-box",
                        borderRadius: 16,
                        padding: "12px 10px",
                        border: `2px solid ${
                          isSelected ? palette.accent : "rgba(255,255,255,0.08)"
                        }`,
                        background: isSelected
                          ? palette.accent
                          : palette.surfaceAlt,
                        color: isSelected ? "#fff" : palette.text,
                        cursor: "pointer",
                        opacity: hasSlots || isSelected ? 1 : 0.72,
                        textAlign: "center",
                        boxShadow: isSelected
                          ? "0 12px 26px rgba(0,0,0,0.18)"
                          : "none",
                        transition: "background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, opacity 0.16s ease, box-shadow 0.16s ease",
                        flex: "0 0 auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-pressed={isSelected}
                    >
                      <span
                        className="d-block"
                        style={{
                          fontSize: "0.78rem",
                          color: isSelected ? "rgba(255,255,255,0.82)" : palette.textMuted,
                          textTransform: "uppercase",
                        }}
                      >
                        
                        {formatWeekday(group.date, t)}
                      </span>
                      <span
                        className="d-block"
                        style={{
                          fontSize: "1.55rem",
                          lineHeight: 1.05,
                          marginTop: 4,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {group.date.getDate()}
                      </span>
                      <span
                        className="d-block fw-500"
                        style={{
                          fontSize: "0.78rem",
                          color: isSelected ? "rgba(255,255,255,0.82)" : palette.textMuted,
                          marginTop: 2,
                        }}
                      >
                        {formatMonth(group.date, t)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 76,
                  zIndex: 1,
                  pointerEvents: "none",
                  background: `linear-gradient(270deg, ${palette.surface} 0%, rgba(0,0,0,0) 100%)`,
                }}
              />
              <button
                type="button"
                onPointerDown={() => handleDayScrollPointer(1)}
                onClick={() => handleDayScrollKeyboardClick(1)}
                onMouseEnter={() => startDayAutoScroll(1)}
                onMouseLeave={stopDayAutoScroll}
                onFocus={() => startDayAutoScroll(1)}
                onBlur={stopDayAutoScroll}
                aria-label="Scroll days right"
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  width: 38,
                  height: 38,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  border: palette.border,
                  background: palette.surface,
                  color: palette.accent,
                  boxShadow: "0 12px 26px rgba(0,0,0,0.18)",
                  cursor: "pointer",
                }}
              >
                <i className="las la-angle-right" aria-hidden="true" />
              </button>
              <style>
                {`.booking-day-strip{scrollbar-width:none;-ms-overflow-style:none}.booking-day-strip::-webkit-scrollbar{display:none}.booking-day-strip button{font-family:"Segoe UI","Noto Sans Armenian",Arial,sans-serif}`}
              </style>
            </div>

            {selectedGroup ? (
              <div style={{ minHeight: 128 }}>
                <div
                  className="fw-700 mb-2"
                  style={{ color: palette.text, fontSize: "1rem" }}
                >
                  {formatDayLabel(selectedGroup.date, lang, t, { relative: true })}
                </div>
                {loading && requestedDayKey ? (
                  <div
                    style={{
                      background: palette.surfaceAlt,
                      border: palette.border,
                      borderRadius: 14,
                      padding: "14px 16px",
                      minHeight: 52,
                      color: palette.textMuted,
                      fontSize: "0.92rem",
                    }}
                  >
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{ color: palette.accent }}
                      aria-hidden="true"
                    />
                    {t("booking.common.loading")}
                  </div>
                ) : selectedGroup.slots.length === 0 && !loading ? (
                  <div
                    style={{
                      background: palette.surfaceAlt,
                      border: palette.border,
                      borderRadius: 14,
                      padding: "14px 16px",
                      minHeight: 52,
                      color: palette.textMuted,
                      fontSize: "0.92rem",
                    }}
                  >
                    <i className="las la-clock me-1" aria-hidden="true" />
                    {t("booking.datetime.noDaySlotsFull")}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(82px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {selectedGroup.slots.map((slot) => {
                      const isSelected =
                        selectedSlot && selectedSlot.raw === slot.raw;
                      return (
                        <button
                          key={slot.raw}
                          type="button"
                          onClick={() => onSelectSlot(slot)}
                          className="fw-700"
                          style={{
                            padding: "10px 12px",
                            borderRadius: 999,
                            background: isSelected
                              ? palette.accent
                              : palette.surfaceAlt,
                            color: isSelected ? "#fff" : palette.text,
                            border: isSelected
                              ? `2px solid ${palette.accent}`
                              : palette.border,
                            cursor: "pointer",
                            fontSize: "0.95rem",
                            fontVariantNumeric: "tabular-nums",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {formatClockTime(slot.date)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Contact / identity inputs */}
      <div
        style={{
          padding: `16px ${STEP_PAD.x} 4px ${STEP_PAD.x}`,
          borderTop: palette.border,
          marginTop: 12,
          display: "grid",
          gap: 12,
        }}
      >
        {showContactInputs ? (
          <>
            {/* Anonymous user — gather contact details so we can email/SMS
                them once the backend wires up notifications. */}
            <div
              style={{
                fontSize: "0.85rem",
                color: palette.textMuted,
                lineHeight: 1.4,
              }}
            >
              <i
                className="las la-info-circle me-1"
                style={{ color: palette.accent, fontSize: "1rem" }}
                aria-hidden="true"
              />
              {t("booking.datetime.contactHint")}
            </div>

            {shouldAskName ? (
              <div>
                <label
                  className="d-block fw-600 mb-1"
                  style={{ color: palette.text, fontSize: "0.9rem" }}
                >
                  {t("booking.datetime.nameLabel")}
                </label>
                <input
                  type="text"
                  value={clientName || ""}
                  onChange={(e) => onClientNameChange(e.target.value)}
                  onBlur={() => setNameTouched(true)}
                  placeholder={t("booking.datetime.namePlaceholder")}
                  style={{
                    width: "100%",
                    background: palette.surfaceAlt,
                    border:
                      nameTouched && !nameValid
                        ? "2px solid hsl(0 80% 55%)"
                        : palette.border,
                    borderRadius: 12,
                    padding: "10px 14px",
                    color: palette.text,
                    fontSize: "0.95rem",
                    outline: "none",
                  }}
                />
                {nameTouched && !nameValid ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: "0.78rem",
                      color: "hsl(0 80% 55%)",
                    }}
                  >
                    {t("booking.datetime.nameRequired")}
                  </div>
                ) : null}
              </div>
            ) : null}

            {shouldAskPhone ? (
              <div>
                <label
                  className="d-block fw-600 mb-1"
                  style={{ color: palette.text, fontSize: "0.9rem" }}
                >
                  {t("booking.datetime.phoneLabel")}
                </label>
                <input
                  type="tel"
                  value={phone || ""}
                  onChange={(e) => onPhoneChange(e.target.value)}
                  onBlur={() => setPhoneTouched(true)}
                  placeholder={t("booking.datetime.phonePlaceholder")}
                  style={{
                    width: "100%",
                    background: palette.surfaceAlt,
                    border:
                      phoneTouched && !phoneValid
                        ? "2px solid hsl(0 80% 55%)"
                        : palette.border,
                    borderRadius: 12,
                    padding: "10px 14px",
                    color: palette.text,
                    fontSize: "0.95rem",
                    outline: "none",
                  }}
                />
                {phoneTouched && !phoneValid ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: "0.78rem",
                      color: "hsl(0 80% 55%)",
                    }}
                  >
                    {t(
                      phoneFilled
                        ? "booking.datetime.phoneInvalid"
                        : "booking.datetime.phoneRequired",
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {shouldAskEmail ? (
              <div>
                <label
                  className="d-block fw-600 mb-1"
                  style={{ color: palette.text, fontSize: "0.9rem" }}
                >
                  {t("booking.datetime.emailLabel")}
                </label>
                <input
                  type="email"
                  value={email || ""}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder={t("booking.datetime.emailPlaceholder")}
                  style={{
                    width: "100%",
                    background: palette.surfaceAlt,
                    border:
                      emailTouched && !emailValid
                        ? "2px solid hsl(0 80% 55%)"
                        : palette.border,
                    borderRadius: 12,
                    padding: "10px 14px",
                    color: palette.text,
                    fontSize: "0.95rem",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    marginTop: 6,
                    fontSize: "0.78rem",
                    color:
                      emailTouched && !emailValid
                        ? "hsl(0 80% 55%)"
                        : palette.textMuted,
                  }}
                >
                  {emailTouched && !emailValid
                    ? t(
                        email
                          ? "booking.datetime.emailInvalid"
                          : "booking.datetime.emailRequired",
                      )
                    : t("booking.datetime.emailHint")}
                </div>
              </div>
            ) : null}
          </>
        ) : isAuthenticated && userLoading ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{
              background: palette.accentSoft,
              color: palette.text,
              border: palette.border,
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: "0.9rem",
            }}
          >
            <i
              className="las la-user-clock"
              style={{ color: palette.accent, fontSize: "1.2rem" }}
              aria-hidden="true"
            />
            <span>{t("booking.datetime.accountLoading")}</span>
          </div>
        ) : accountEmail || accountPhone ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{
              background: palette.accentSoft,
              color: palette.text,
              border: palette.border,
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: "0.9rem",
            }}
          >
            <i
              className="las la-user-check"
              style={{ color: palette.accent, fontSize: "1.2rem" }}
              aria-hidden="true"
            />
            <span>
              {t("booking.datetime.loggedInHint", {
                email: accountEmail || accountPhone,
              })}
            </span>
          </div>
        ) : null}

        <div>
          <label
            className="d-block fw-600 mb-1"
            style={{ color: palette.text, fontSize: "0.9rem" }}
          >
            {t("booking.datetime.commentLabel")}
          </label>
          <textarea
            value={clientComment || ""}
            onChange={(e) => onClientCommentChange(e.target.value)}
            placeholder={t("booking.datetime.commentPlaceholder")}
            rows={3}
            style={{
              width: "100%",
              minHeight: 92,
              resize: "vertical",
              background: palette.surfaceAlt,
              border: palette.border,
              borderRadius: 12,
              padding: "10px 14px",
              color: palette.text,
              fontSize: "0.95rem",
              outline: "none",
            }}
          />
        </div>
      </div>

      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canContinue}
          className="pill fw-500"
          style={{
            background: canContinue ? palette.accent : palette.surfaceAlt,
            color: canContinue ? "#fff" : palette.textMuted,
            border: canContinue ? "none" : palette.border,
            padding: "10px 24px",
            opacity: canContinue ? 1 : 0.5,
            cursor: canContinue ? "pointer" : "not-allowed",
          }}
        >
          {t("booking.common.confirm")}
          <i className="las la-check ms-2" aria-hidden="true" />
        </button>
      </StepFooter>
    </StepCard>
  );
}
